import type { ReactNode } from 'react';

import { Tabs, Heading } from '@checkatrade/web-ui';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { BasicLinksGroupItem, LinksGroupItem } from 'lib/utils/seo/types';
import { isBasicLinksGroupItem } from 'lib/utils/seo/types';
import useIsMobileScreen from '../../../../@hooks/use-is-mobile-screen';
import { Accordion } from '../../../../lib/mortar/components/Accordion';
import type { Block, Inline } from '@contentful/rich-text-types';
import { BLOCKS } from '@contentful/rich-text-types';
import { IconAdvice } from '../../../../@toolshed/styleguide/components/AppIcon/AppIcons';

interface InterlinksProps {
  interlinks: LinksGroupItem[] | undefined;
  category: string;
  location: string;
}

const tabsUlClasses = 'columns-3 py-8 gap-4';
const accordianUlClasses = 'list-none columns-1 py-4';
const liClasses = 'text-neutral-60 pb-3 leading-6 flex';

const tabsOptions = {
  renderNode: {
    [BLOCKS.UL_LIST]: (_node: Block | Inline, children: ReactNode) => (
      <ul className={tabsUlClasses}>{children}</ul>
    ),
    [BLOCKS.LIST_ITEM]: (_node: Block | Inline, children: ReactNode) => (
      <li className={liClasses}>{children}</li>
    ),
  },
};
const accordianOptions = {
  renderNode: {
    [BLOCKS.UL_LIST]: (_node: Block | Inline, children: ReactNode) => (
      <ul className={accordianUlClasses}>{children}</ul>
    ),
    [BLOCKS.LIST_ITEM]: (_node: Block | Inline, children: ReactNode) => (
      <li className={liClasses}>{children}</li>
    ),
  },
};

const BasicLinksGroupItemContent = (
  interlink: BasicLinksGroupItem,
  ulClasses: string,
) => {
  return interlink.links ? (
    <ul className={ulClasses}>
      {interlink.links.map(link => (
        <li key={link.url} className={liClasses}>
          <a href={link.url}>{link.title}</a>
        </li>
      ))}
    </ul>
  ) : null;
};

export const Interlinks = (props: InterlinksProps) => {
  const isMobile = useIsMobileScreen('768px');
  const { interlinks, location } = props;
  const tabContent = interlinks?.map(interlink => ({
    id: interlink.key || '',
    label: interlink.heading || '',
    name: interlink.heading || '',
    content: isBasicLinksGroupItem(interlink)
      ? BasicLinksGroupItemContent(
          interlink,
          isMobile ? accordianUlClasses : tabsUlClasses,
        )
      : interlink.links?.json &&
        documentToReactComponents(
          interlink.links.json,
          isMobile ? accordianOptions : tabsOptions,
        ),
  }));

  if (!tabContent || tabContent.length === 0) {
    return null;
  }

  const accordianContent = tabContent?.map(({ label, content }) => ({
    heading: label || '',
    name: label || '',
    child: content,
  }));

  return (
    <>
      <div className="flex-column border-stroke-interactive mt-4 flex w-screen justify-center border-t-4 bg-neutral-100" />
      <div className="m-auto flex max-w-screen-lg justify-center">
        <div className="mb-12 mt-20 w-[calc(100%-2rem)]">
          <Heading
            as="h3"
            variant="titleLG"
            weight="semibold"
            className="flex gap-4 pb-4 text-blue-950"
          >
            <IconAdvice size={'large'} />
            Looking for more tradespeople in {location}?
          </Heading>

          <p className="mb-16 text-neutral-60">
            Find Checkatrade reviewed Tradespeople, get a quote today, discover
            related services in {location} and check out our latest home
            improvement guides.
          </p>
          {isMobile ? (
            <Accordion headingLevels="h3" items={accordianContent} />
          ) : (
            <Tabs name={'interlinksTabs'} tabs={tabContent} />
          )}
        </div>
      </div>
    </>
  );
};

export default Interlinks;
